import { useMemo } from "react";
import { Tag } from "@/components/ui/tag";
import { useDisciplines } from "@/hooks/use-disciplines";
import { DEFAULT_DISCIPLINE_COLOR } from "@/lib/discipline-colors";
import { cn } from "@/lib/utils";

// Resolves the curated color for each Discipline code from the library. Returns a
// Map<code, hex>; codes with no match fall back to the default slate. Used wherever a
// Discipline chip/dot is rendered so color stays consistent without threading the hex
// through every API payload (the library is small and the query is cached/deduped).
export function useDisciplineColorMap(): Map<string, string> {
  const { data } = useDisciplines({ includeInactive: true });
  return useMemo(() => new Map((data ?? []).map((d) => [d.code, d.color])), [data]);
}

// Compact Discipline chip: a color dot + the code. Color is a UX aid only — Discipline
// does not affect Fit Score. The code text stays foreground for legibility in both themes;
// color is carried by the dot, border tint, and background tint.
export function DisciplineChip({
  code,
  title,
  helpText,
  className,
}: {
  code: string;
  title?: string;
  // Per-level rating guidance (from discipline_rating_level). When set, it wins the native
  // tooltip so hovering the chip reveals what the Resource's held rating level means.
  helpText?: string | null;
  className?: string;
}) {
  const colors = useDisciplineColorMap();
  const color = colors.get(code) ?? DEFAULT_DISCIPLINE_COLOR;
  return (
    <Tag
      variant="stroke"
      title={helpText ?? title ?? code}
      // Compact density override: these chips sit inside dense grids/tables, so
      // they keep their small mono styling on top of the NOS Tag base.
      className={cn(
        "h-auto gap-1 rounded-sm px-1.5 py-0.5 font-mono text-xs font-semibold tracking-wide text-foreground",
        className,
      )}
      // Data-driven Discipline color — the documented §4 exception (curated in the
      // Discipline library at runtime, not part of the static token palette).
      style={{ borderColor: `${color}59`, backgroundColor: `${color}1f` }}
      icon={
        <span className="h-1.5 w-1.5 rounded-full" style={{ backgroundColor: color }} aria-hidden />
      }
    >
      {code}
    </Tag>
  );
}
